import { useMemo, useState, useEffect, useCallback, Suspense } from 'react';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router';

import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PianoAnalyticsProvider } from '@sdx/b2o-fe-tools/pianoAnalytics';

import { msalInstance, initializeMSAL } from '../msalConfig';

import { Provider, Flex } from './components/ui/provider';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuth } from './core/contexts/AuthContext';
import { FeedbackDialogProvider, useFeedbackDialog } from './core/contexts/FeedbackDialogContext';
import { FeedbackType } from './components/FeedbackDialog/FeedbackDialog.types';
import { OsatModalProvider, useOsatModal } from './core/contexts/OsatModalContext';
import { UserProvider, useUser } from './core/contexts/UserContext';
import { useOsatEligibility } from './core/hooks/useOsatEligibility';
import { useDismissOsat } from './core/hooks/useDismissOsat';
import { useBestEffortPiano } from './core/hooks/useBestEffortPiano';
import { getOsatEvaluated, markOsatEvaluated, markOsatShown } from './core/constants/osat';
import PopupBannerHost from './features/PopupBanner/PopupBannerHost';
import type { OsatDismissReason } from './components/OsatModal/OsatModal.types';
import { useWorkforceSizingPermission } from './core/hooks/useWorkforceSizingPermission';
import { useGesPermission } from './core/hooks/useGesPermission';
import { useIntegratedManagementPermission } from './core/hooks/useIntegratedManagementPermission';
import { useAdminPanelPermission } from './core/hooks/useAdminPanelPermission';
import { usePasPermission } from './core/hooks/usePasPermission';
import {
  PermissionGuard,
  toAccessDecision,
} from './core/components/PermissionGuard/PermissionGuard';
import { resolveWorkforceAccess } from './features/WorkforceSizing/utils/resolveWorkforceAccess';
import { resolveIntegratedManagementAccess } from './features/IntegratedManagementModule/utils/resolveIntegratedManagementAccess';
import { resolveAdmPanelAccess } from './features/AdminPanelModule/utils/resolveAdmPanelAccess';
import { ADM_PANEL_ROUTE_PATTERN } from './core/constants/admPanelRoutes';
import { resolvePasAccess } from './features/PasModule/utils/resolvePasAccess';
import { resolveMyTasksAccess } from './features/PasModule/utils/resolveMyTasksAccess';
import PasNavigationBoundary from './features/PasModule/PasNavigationBoundary';
import { PAS_ROUTE_PATTERN } from './core/constants/pasRoutes';
import { SUPPORT_CENTER_ROUTE_PATTERN } from './core/constants/supportCenterPaths';
import { AppProvider } from './core/contexts/AppContext';
import telemetryConfig from './core/services/telemetry/telemetryConfig';
import { TelemetryProvider } from './core/services/telemetry/telemetryProvider';
import {
  startDatadogView,
  addDatadogError,
  normalizeErrorForTelemetry,
} from './core/services/telemetry/datadog';
import { resolveDatadogViewName } from './core/services/telemetry/resolveDatadogViewName';
import ApiProvider from './components/ApiProvider/ApiProvider';
import { MODULE_NAME, PIANO_EVENTS } from './core/constants/pianoEvents';

import ErrorBoundary from './features/Error/ErrorBoundary';
import SupportCenterGate from './features/SupportCenter/components/SupportCenterGate';
import Navbar from './components/Navbar/Navbar';
import ChatbotWrapper from './components/ChatbotWrapper/ChatbotWrapper';

import { I18nextProvider } from 'react-i18next';
import i18n from './core/i18n/i18n.config';
import { useBackendTranslations } from './core/hooks/useBackendTranslations';
import { useLanguagePreference } from './core/hooks/useLanguagePreference';
import { useAppShellGate } from './core/hooks/useAppShellGate';
import AppShellSkeleton from './components/AppShellSkeleton/AppShellSkeleton';
import { retryUnless401 } from './core/utils/retryPolicy';

import { MOBILE_NAV_HEIGHT } from './core/constants/layout';

import './App.scss';
import './theme/variables.scss';

const GesPage = React.lazy(() => import('./features/GES/pages/GesPage'));
const AboutSodexoPage = React.lazy(() => import('./features/GES/pages/AboutSodexo/AboutSodexo'));
const AboutManagementPage = React.lazy(
  () => import('./features/GES/pages/AboutManagement/AboutManagement'),
);
const SettingsPage = React.lazy(() => import('./features/Settings/pages/SettingsPage'));
const MaintenancePage = React.lazy(() => import('./features/Maintenance/pages/MaintenancePage'));
const AccessibilityPage = React.lazy(
  () => import('./features/Accessibility/pages/AccessibilityPage'),
);
const WorkforceSizingPage = React.lazy(
  () => import('./features/WorkforceSizing/pages/WorkforceSizingPage'),
);
const DashboardsPage = React.lazy(() => import('./features/Dashboards/pages/DashboardsPage'));
const SupportCenterPage = React.lazy(
  () => import('./features/SupportCenter/pages/SupportCenterPage'),
);
const EContratPage = React.lazy(() => import('./features/EContrat/pages/EContratPage'));
const IntegratedManagementModule = React.lazy(
  () => import('./features/IntegratedManagementModule/IntegratedManagementModule'),
);

const BreadcrumbsSetup = React.lazy(
  () => import('./core/components/BreadcrumbsSetup/BreadcrumbsSetup'),
);
const PendingChangesDialog = React.lazy(
  () => import('./components/BreadcrumbMenu/PendingChangesDialog'),
);
const FeedbackDialog = React.lazy(() => import('./components/FeedbackDialog/FeedbackDialog'));
const OsatModal = React.lazy(() =>
  import('./components/OsatModal').then((m) => ({ default: m.OsatModal })),
);

const EditFavAppsPage = React.lazy(() => import('./features/FavAppsModule/pages/EditFavAppsPage'));
const Homepage = React.lazy(() => import('./features/Home/pages/Homepage'));
const LoginPage = React.lazy(() => import('./features/Login/pages/LoginPage'));
const SearchResultsPage = React.lazy(
  () => import('./features/SearchModule/pages/SearchResultsPage'),
);

const AdminPanelModule = React.lazy(() => import('./features/AdminPanelModule/AdminPanelModule'));
const PasModule = React.lazy(() => import('./features/PasModule/PasModule'));
const PasMyTasksWidget = React.lazy(() => import('./features/PasModule/PasMyTasksWidget'));
const PasWidgetRegistration = React.lazy(
  () => import('./features/PasModule/PasWidgetRegistration'),
);

type ReactNodeChildren = { children: React.ReactNode };

// Datadog view-name mapping lives in `core/services/telemetry/
// resolveDatadogViewName.ts` so it is unit-testable. When adding a new
// <Route> below, add the matching entry to DATADOG_VIEW_NAMES there in
// the same PR.

const AppRoutes = () => {
  const { isFullyAuthenticated, isSsoAttempting, isAuthInitializing, flags, accessToken } =
    useAuth();
  const { siteCode, siteSegment } = useUser();
  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;
  const { isChecking: isWorkforceChecking, hasAccess: hasWorkforceAccess } =
    useWorkforceSizingPermission({
      accessToken,
      baseUrl,
      siteCode: siteCode ?? undefined,
    });

  // Workforce sizing (Dimensionamento) is restricted to FOOD cost centers.
  // The workforce-sizing permission alone does not encode that rule, so FM users
  // could reach the feature via a direct URL — gate on the segment too (800526).
  const workforceDecision = resolveWorkforceAccess({
    flagEnabled: flags?.ENABLE_WORKFORCE_SIZING === true,
    isChecking: isWorkforceChecking,
    hasPermission: hasWorkforceAccess === true,
    siteSegment,
  });

  const workforceSizingRoute = (
    <PermissionGuard decision={workforceDecision}>
      <WorkforceSizingPage />
    </PermissionGuard>
  );

  const { isChecking: isIntegratedManagementChecking, hasAccess: hasIntegratedManagementAccess } =
    useIntegratedManagementPermission({
      accessToken,
      baseUrl,
      siteCode: siteCode ?? undefined,
    });

  const integratedManagementDecision = resolveIntegratedManagementAccess({
    flagEnabled: flags?.ENABLE_INTEGRATED_MANAGEMENT === true,
    isChecking: isIntegratedManagementChecking,
    hasPermission: hasIntegratedManagementAccess === true,
  });

  const { isChecking: isAdmPanelChecking, hasAccess: hasAdmPanelAccess } = useAdminPanelPermission({
    accessToken,
    baseUrl,
    siteCode: siteCode ?? undefined,
  });

  const admPanelDecision = resolveAdmPanelAccess({
    flagEnabled: flags?.ENABLE_ADM_PANEL === true,
    isChecking: isAdmPanelChecking,
    hasPermission: hasAdmPanelAccess === true,
  });

  const admPanelRoute = (
    <PermissionGuard decision={admPanelDecision}>
      <AdminPanelModule />
    </PermissionGuard>
  );

  const { isChecking: isPasChecking, hasAccess: hasPasAccess } = usePasPermission({
    accessToken,
    baseUrl,
    siteCode: siteCode ?? undefined,
  });

  const pasDecision = resolvePasAccess({
    flagEnabled: flags?.ENABLE_PAS === true,
    isChecking: isPasChecking,
    hasPermission: hasPasAccess === true,
  });

  const pasRoute = (
    <PermissionGuard decision={pasDecision}>
      <PasModule />
    </PermissionGuard>
  );

  const myTasksRoute = (
    <PermissionGuard decision={resolveMyTasksAccess(integratedManagementDecision, pasDecision)}>
      <PasNavigationBoundary>
        {pasDecision === 'allow' && <PasWidgetRegistration />}
        {integratedManagementDecision === 'allow' ? (
          <IntegratedManagementModule showPasWidget={pasDecision === 'allow'} />
        ) : (
          <PasMyTasksWidget />
        )}
      </PasNavigationBoundary>
    </PermissionGuard>
  );

  const { isChecking: isGesChecking, hasAccess: hasGesAccess } = useGesPermission({
    accessToken,
    baseUrl,
    siteCode: siteCode ?? undefined,
  });

  const gesDecision = toAccessDecision(isGesChecking, hasGesAccess);
  const gesRoutes = (
    <Route
      element={
        <PermissionGuard decision={gesDecision}>
          <Outlet />
        </PermissionGuard>
      }
    >
      <Route path='/ges' element={<GesPage />} />
      <Route path='/ges/about-sodexo' element={<AboutSodexoPage />} />
      <Route path='/ges/about-management' element={<AboutManagementPage />} />
    </Route>
  );

  const authenticatedRoutes = (
    <>
      <Route path='/' element={<Homepage />} />
      <Route path='/home' element={<Navigate to='/' replace />} />
      <Route path='/apps/edit' element={<EditFavAppsPage />} />
      <Route path='/apps/search' element={<SearchResultsPage />} />
      <Route path='/settings' element={<SettingsPage />} />
      {/* Open to any authenticated user — no PermissionGuard. The country rule lives on the
          sidebar link that points here (#871925), never on the route itself (#871923). */}
      <Route path='/accessibility' element={<AccessibilityPage />} />
      {gesRoutes}
      <Route path='/workforce-sizing' element={workforceSizingRoute} />
      <Route path='/dashboards' element={<DashboardsPage />} />
      {/* Open to any authenticated user: visibility is the Strapi menu record and SAP enforces access inside the frame (#1064927). */}
      <Route path='/econtrat' element={<EContratPage />} />
      <Route path='/my-tasks' element={myTasksRoute} />
      <Route path={ADM_PANEL_ROUTE_PATTERN} element={admPanelRoute} />
      <Route path={PAS_ROUTE_PATTERN} element={pasRoute} />
      <Route
        path={SUPPORT_CENTER_ROUTE_PATTERN}
        element={
          <SupportCenterGate>
            <SupportCenterPage />
          </SupportCenterGate>
        }
      />
      <Route path='*' element={<Navigate to='/' replace />} />
    </>
  );

  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  useBackendTranslations();
  const { isSettled: isLanguageSettled } = useLanguagePreference();

  // Drive named RUM views from React Router so dashboards group views by
  // logical route name (e.g. "ges.about-management") rather than raw URL.
  // Only the canonical (already normalized/bucketed) view name is attached —
  // NOT the raw pathname — so dynamic segments (contract/site ids) don't leak
  // into a custom context attribute or inflate cardinality. RUM still records
  // the URL natively in `view.url`. No-op when Datadog isn't initialized.
  useEffect(() => {
    startDatadogView({ name: resolveDatadogViewName(location.pathname) });
  }, [location.pathname]);

  const { open: openOsat } = useOsatModal();
  // Eligibility is gated on full authentication (never fetched during SSO /
  // auth init) AND the `ENABLE_OSAT_MODAL` feature flag — a runtime kill-switch
  // (mirrors `useAlertBar`): off/absent ⇒ enabled:false ⇒ no eligibility network
  // call and, since the open effect below guards on `isFetched`, the modal never
  // opens. Do NOT gate the modal on `isFullyAuthenticated` alone — that is true
  // on every refresh; the write-once `osat:evaluated` session flag (set on the
  // first settled eligibility) is the discriminator (#806378, 859323).
  const { data: osatEligibility, isFetched: isEligibilityFetched } = useOsatEligibility(
    isFullyAuthenticated && flags?.ENABLE_OSAT_MODAL === true,
  );

  // Open the OSAT survey once per browser session, on the FIRST settled
  // eligibility after full authentication — INDEPENDENT of auth path (interactive
  // redirect, ssoSilent, or acquireTokenSilent). It previously required an
  // interactive-login marker (osat:freshLogin) set from handleRedirectPromise,
  // which silent auth never returns through, so the corporate-SSO / returning-user
  // cohort was never surveyed (859323). The write-once `osat:evaluated` flag
  // replaces it: set as soon as the query SETTLES — a completed fetch, on SUCCESS
  // OR ERROR — so an F5 in the same tab session does not re-show (#806378, the
  // flag persists), while a new tab / next-day session re-evaluates (server-side
  // same-day suppression is the once-per-day authority). `isFetched` is the settle
  // signal: false while the query is disabled (enabled:false, not fully
  // authenticated) or its first fetch is in flight, so the flag is never set
  // prematurely; TanStack leaves `data===undefined` on a settled error, so the
  // flag is still set on that branch and a later success flip cannot reopen
  // without a new session.
  useEffect(() => {
    // Defence-in-depth: only evaluate while genuinely authenticated with the flag on.
    // TanStack v5 keeps `isFetched` sticky-true even after the query is disabled, so a
    // logout / kill-switch could otherwise let this run off stale cached eligibility
    // (859323 regression). The auth-clear paths also drop the eligibility cache.
    if (!isFullyAuthenticated || flags?.ENABLE_OSAT_MODAL !== true) return;
    if (!isEligibilityFetched) return; // still loading OR query disabled → do nothing yet
    if (getOsatEvaluated()) return; // already evaluated this tab-session (cleared on logout)
    markOsatEvaluated(); // mark once SETTLED (success or error)
    if (osatEligibility?.eligible === true) {
      // #885563: distinct from markOsatEvaluated above, which fires on any settle. The pop-up
      // banner yields to the survey for the rest of the session, so it needs "actually opened".
      markOsatShown();
      openOsat();
    }
  }, [
    isFullyAuthenticated,
    flags?.ENABLE_OSAT_MODAL,
    isEligibilityFetched,
    osatEligibility,
    openOsat,
  ]);

  const { isGated, showSkeleton } = useAppShellGate(isLanguageSettled);

  if (isGated) {
    return showSkeleton ? <AppShellSkeleton /> : null;
  }

  const loginRedirect = <Navigate to='/login' state={{ from: location }} replace />;

  return (
    <Flex alignItems='flex-start' w='100%' h='100vh' overflow='hidden' bgColor='whiteAlpha.solid'>
      {isFullyAuthenticated && !isLoginPage && <Navbar />}

      <Flex
        direction='column'
        flex='1'
        h='100vh'
        overflow='auto'
        pb={isFullyAuthenticated ? { base: MOBILE_NAV_HEIGHT, md: '0' } : undefined}
      >
        {import.meta.env.VITE_FEATURE_FLAG_AUTH_REDIRECT === 'true' && (
          <>
            <Suspense fallback={null}>
              <Routes>
                <Route path='/login' element={<LoginPage />} />
                <Route
                  path='*'
                  element={
                    <>
                      <UnauthenticatedTemplate>{loginRedirect}</UnauthenticatedTemplate>

                      <AuthenticatedTemplate>
                        {isFullyAuthenticated ? (
                          <Routes>{authenticatedRoutes}</Routes>
                        ) : isSsoAttempting || isAuthInitializing ? null : (
                          loginRedirect
                        )}
                      </AuthenticatedTemplate>
                    </>
                  }
                />
              </Routes>
            </Suspense>
          </>
        )}

        {import.meta.env.VITE_FEATURE_FLAG_AUTH_REDIRECT === 'false' && (
          <Suspense fallback={null}>
            <Routes>
              <Route path='/login' element={<Navigate to='/' replace />} />
              {authenticatedRoutes}
            </Routes>
          </Suspense>
        )}
      </Flex>

      <Suspense fallback={null}>
        <PendingChangesDialog />
      </Suspense>
    </Flex>
  );
};

// MSAL initialization wrapper with proper async handling
const MSALWrapper = ({ children }: ReactNodeChildren) => {
  // When msalInstance is already initialized (e.g. in tests via mock), skip
  // the async wait so children render immediately.
  const alreadyReady =
    typeof (msalInstance as { getAllAccounts?: unknown }).getAllAccounts !== 'function';
  const [isReady, setIsReady] = useState(alreadyReady);

  useEffect(() => {
    if (alreadyReady) return;

    let isMounted = true;

    initializeMSAL()
      .then(() => {
        if (isMounted) {
          setIsReady(true);
        }
      })
      .catch((err) => {
        // forwardErrorsToLogs (datadog.ts) forwards console.error to Datadog
        // Logs on deployed builds, and MSAL errors can embed account/tenant
        // hints — so never pass the raw error to console here. Log + forward
        // only the scrubbed version (generic message + name/errorCode).
        const { error: safeError, context } = normalizeErrorForTelemetry(err, {
          genericMessage: 'MSAL initialization failed',
        });
        console.error('MSAL initialization failed:', safeError);
        addDatadogError(safeError, { source: 'App.MSALWrapper.initializeMSAL', ...context });
        // Set ready anyway to prevent infinite loading
        if (isMounted) setIsReady(true);
      });

    return () => {
      isMounted = false;
    };
  }, [alreadyReady]); // alreadyReady is derived from msalInstance (module-level constant) — never changes, equivalent to []

  if (!isReady) {
    return null;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

const BasicProviders = ({ children }: ReactNodeChildren) => (
  <Provider>
    <Toaster />
    {children}
  </Provider>
);

const CoreProviders = ({ children }: ReactNodeChildren) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: retryUnless401(3),
          },
        },
      }),
    [],
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

const I18nProvider = ({ children }: ReactNodeChildren) => (
  <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
);

const PianoProviderWrapper = ({ children }: ReactNodeChildren) => {
  const { accessToken, isFullyAuthenticated } = useAuth();
  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;

  return (
    <PianoAnalyticsProvider
      baseUrl={baseUrl}
      accessToken={isFullyAuthenticated ? (accessToken ?? '') : ''}
      moduleName={MODULE_NAME}
      env={String(import.meta.env.VITE_APP_ENVIRONMENT)}
      pianoConfig={{
        siteId: String(import.meta.env.VITE_APP_PIANO_SITE_ID ?? ''),
        collectionDomain: String(import.meta.env.VITE_APP_PIANO_COLLECTION_DOMAIN ?? ''),
      }}
    >
      {children}
    </PianoAnalyticsProvider>
  );
};

const AppProviders = ({ children }: ReactNodeChildren) => {
  return (
    <AuthProvider>
      <UserProvider initialAppId='B2O-container-app-web'>
        <ApiProvider>
          <PianoProviderWrapper>{children}</PianoProviderWrapper>
        </ApiProvider>
      </UserProvider>
    </AuthProvider>
  );
};

const RouterWrapper = ({ children }: ReactNodeChildren) => (
  <BrowserRouter>
    <Suspense fallback={null}>
      <BreadcrumbsSetup>{children}</BreadcrumbsSetup>
    </Suspense>
  </BrowserRouter>
);

const FeedbackDialogWrapper = ({ children }: ReactNodeChildren) => (
  <FeedbackDialogProvider>
    {children}
    <FeedbackDialog />
  </FeedbackDialogProvider>
);

// Bridges the OSAT modal (visibility from OsatModalContext) to the dismiss
// action. Both dismiss affordances (X / "Responder depois" / Esc) route through
// the modal's `onClose(reason)` → PATCH /osat/dismiss then close. A submit is
// not a dismiss, so `onSubmitted` only closes (no PATCH).
const OsatModalHost = (): React.JSX.Element => {
  const { isOpen, close } = useOsatModal();
  const { showFeedbackDialog } = useFeedbackDialog();
  const { mutate: dismissOsat, isPending: isDismissing } = useDismissOsat();
  const { trackClick } = useBestEffortPiano();

  // Dismiss records same-day suppression server-side (PATCH /osat/dismiss), so
  // only close once it succeeds. On failure keep the modal open so the user can
  // retry — closing anyway would swallow the error and let the survey re-show on
  // the next fresh login the same day (#806378).
  const handleDismiss = useCallback(
    (reason: OsatDismissReason): void => {
      // Pending guard: rapid X / Esc / "Responder depois" clicks must not enqueue
      // a second PATCH /osat/dismiss while one is already in flight (#806378).
      if (isDismissing) return;
      dismissOsat(undefined, {
        onSuccess: () => {
          // Tag on the SUCCESS path (#826249) so the dismiss funnel counts only
          // dismissals that actually landed — a failed / retried PATCH keeps the
          // modal open, so tagging on click would record a phantom dismissal and
          // double-count the retry. `reason` selects the catalog event.
          trackClick(
            reason === 'later' ? PIANO_EVENTS.OSAT.RESPOND_LATER : PIANO_EVENTS.OSAT.CLOSE,
          );
          close();
        },
        // Keep the modal open on failure (no `close`) and surface feedback using
        // the same dialog the submit success path uses, so the user is not left
        // with a silently-unresponsive dismiss.
        onError: () => showFeedbackDialog('common:osat.toast.error.message', FeedbackType.ERROR),
      });
    },
    [dismissOsat, close, isDismissing, showFeedbackDialog, trackClick],
  );

  return (
    <Suspense fallback={null}>
      <OsatModal
        isOpen={isOpen}
        onClose={handleDismiss}
        onSubmitted={close}
        isDismissing={isDismissing}
      />
    </Suspense>
  );
};

const OsatModalWrapper = ({ children }: ReactNodeChildren) => (
  <OsatModalProvider>
    {children}
    <OsatModalHost />
  </OsatModalProvider>
);

// Observability layer - telemetry tracking and error boundaries
const ObservabilityProviders = ({ children }: ReactNodeChildren) => {
  const validTelemetryConfig = useMemo(
    () => ({
      ...telemetryConfig,
      instrumentationKey:
        telemetryConfig.instrumentationKey || import.meta.env.VITE_APPINSIGHTS_KEY || '',
    }),
    [],
  );

  return (
    <TelemetryProvider config={validTelemetryConfig}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </TelemetryProvider>
  );
};

const App = (): React.JSX.Element => {
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  const checkMaintenanceBypass = () => {
    try {
      const urlParams = new URLSearchParams(globalThis.location.search);
      const bypassParam = urlParams.get('bypass-maintenance');

      if (bypassParam === 'true') {
        localStorage.setItem('maintenanceBypass', 'true');
        return true;
      }

      return localStorage.getItem('maintenanceBypass') === 'true';
    } catch {
      return false;
    }
  };

  const shouldShowMaintenance = isMaintenanceMode && !checkMaintenanceBypass();

  if (shouldShowMaintenance) {
    return (
      <BasicProviders>
        <CoreProviders>
          <I18nProvider>
            <RouterWrapper>
              <AppProviders>
                <Suspense fallback={null}>
                  <MaintenancePage />
                </Suspense>
              </AppProviders>
            </RouterWrapper>
          </I18nProvider>
        </CoreProviders>
      </BasicProviders>
    );
  }

  return (
    <MSALWrapper>
      <BasicProviders>
        <CoreProviders>
          <I18nProvider>
            <RouterWrapper>
              <AppProviders>
                <ObservabilityProviders>
                  <FeedbackDialogWrapper>
                    <OsatModalWrapper>
                      <AppProvider>
                        <AppRoutes />
                        <ChatbotWrapper />
                        <PopupBannerHost />
                      </AppProvider>
                    </OsatModalWrapper>
                  </FeedbackDialogWrapper>
                </ObservabilityProviders>
              </AppProviders>
            </RouterWrapper>
          </I18nProvider>
        </CoreProviders>
      </BasicProviders>
    </MSALWrapper>
  );
};

export default App;
